import {z} from 'zod';

/**
 * Zod schemas for PebblePlot Scatter Plot specification
 */

// Axis configuration schema
const axisSchema = z.object({
  title: z.string().optional(), // Optional: Axis title/label
  min: z.number().optional(), // Optional: Minimum value (auto if not provided)
  max: z.number().optional(), // Optional: Maximum value (auto if not provided)
  tickStep: z.number().optional(), // Optional: Step size between ticks (auto if not provided)
  showGrid: z.boolean().optional().default(true), // Optional: Show grid lines
  showLabels: z.boolean().optional().default(true), // Optional: Show tick labels
  crossAt: z.number().optional().default(0), // Optional: Where the other axis crosses this axis (default: 0)
  crossAt2: z.number().optional(), // Optional: Where the second Y axis crosses the X axis (used for y2 axis placement)
});

// Legend configuration schema
const legendSchema = z.object({
  enabled: z.boolean().optional().default(false), // Optional: Enable legend rendering (default: false)
  position: z.enum(['top', 'bottom', 'left', 'right']).optional().default('top'), // Optional: Legend position
});

// Line configuration schema
const lineSchema = z.object({
  color: z.string().optional(), // Optional: Line color (e.g., "#1976d2", "primary.main", default: primary.dark)
  strokeWidth: z.number().optional().default(2), // Optional: Line stroke width in pixels (default: 2)
  strokeDasharray: z.string().optional(), // Optional: Line dash pattern (e.g., "5,5" for dashed, undefined for solid)
});

const equationLineRangeSchema = z.object({
  min: z.number(),
  max: z.number(),
  step: z.number().optional(),
});

const equationLineSchema = z
  .object({
    equation: z.string().optional(),
    equationType: z.enum(['function', 'parametric', 'circle']).optional().default('function'),
    range: equationLineRangeSchema.optional(),

    // Styling (similar to point labels)
    color: z.string().optional(),
    label: z.string().optional(),
    labelFontSize: z.union([z.string(), z.number()]).optional(),
    labelFontColor: z.string().optional(),
    strokeWidth: z.number().optional().default(2),
    strokeDasharray: z.string().optional(),

    // Circle params (for equationType='circle')
    centerX: z.number().optional(),
    centerY: z.number().optional(),
    radius: z.number().optional(),
  })
  .superRefine((value, ctx) => {
    const equationType = value.equationType ?? 'function';

    if (equationType === 'circle') {
      if (value.centerX === undefined || value.centerY === undefined || value.radius === undefined) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: 'circle requires centerX, centerY and radius',
          path: ['equationType'],
        });
      }
      return;
    }

    if (!value.equation || value.equation.trim().length === 0) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'equation is required for function/parametric',
        path: ['equation'],
      });
    }
  });

const svgPathSchema = z.object({
  svgPath: z.string(), // SVG path `d` attribute in plot-pixel coordinates (0..plotWidth, 0..plotHeight)
  color: z.string().optional(),
  strokeWidth: z.number().optional().default(2),
  strokeDasharray: z.string().optional(),
  fill: z.string().optional(),
  label: z.string().optional(),
  labelFontSize: z.union([z.string(), z.number()]).optional(),
  labelFontColor: z.string().optional(),
});

// Data point schema
const scatterDataPointSchema = z.object({
  x: z.number(), // Required: X coordinate
  y: z.number(), // Required: Y coordinate
  label: z.string().optional(), // Optional: Label text for the point
  labelFontSize: z.union([z.string(), z.number()]).optional(), // Optional: Label font size (e.g., "12px", "0.9rem", or number)
  labelFontColor: z.string().optional(), // Optional: Label font color (e.g., "#000000", "primary.main")
  pointSize: z.number().optional(), // Optional: Point size in pixels (default: 6)
  pointColor: z.string().optional(), // Optional: Point color (e.g., "#1976d2", "primary.main")
  pointShape: z.enum(['circle', 'square', 'triangle', 'diamond', 'cross']).optional().default('circle'), // Optional: Point shape
});

// Dataset schema (multiple series support)
const scatterDataSetSchema = z.object({
  label: z.string().optional(), // Optional: label shown in legend
  isY2Axis: z.boolean().optional().default(false), // Optional: map this series to y2Axis (default: false)
  data: z.array(scatterDataPointSchema).min(1), // Required: series points
});

const scatterDataSchema = z.union([z.array(scatterDataPointSchema).min(1), z.array(scatterDataSetSchema).min(1)]);

// Scatter plot graph schema
export const pebbleplotScatterSchema = z
  .object({
    type: z.literal('scatter'),
    /**
     * Supports either:
     * - ScatterDataPoint[] (legacy single-series), OR
     * - ScatterDataSet[] (multi-series, preferred)
     */
    data: scatterDataSchema.optional(),
    equationLines: z.array(equationLineSchema).min(1).optional(),
    svgPaths: z.array(svgPathSchema).min(1).optional(),
    xAxis: axisSchema.optional(),
    yAxis: axisSchema.optional(),
    y2Axis: axisSchema.optional(),
    legend: legendSchema.optional(),
    line: lineSchema.optional(), // Optional: Line configuration for connecting points
    width: z.number().optional(), // Optional: SVG width in pixels (default: auto/container width)
    height: z.number().optional(), // Optional: SVG height in pixels (default: 400)
  })
  .refine(
    (value) => {
      const dataValue = value.data as unknown;
      const dataCount = (() => {
        if (!Array.isArray(dataValue) || dataValue.length === 0) return 0;
        const first = dataValue[0];
        if (first && typeof first === 'object' && 'x' in (first as any) && 'y' in (first as any)) {
          // Legacy single-series point array
          return dataValue.length;
        }

        // Dataset array
        return dataValue.reduce<number>((acc, item) => {
          if (!item || typeof item !== 'object') return acc;
          if ('data' in item && Array.isArray((item as {data?: unknown}).data)) {
            return acc + ((item as {data: unknown[]}).data.length ?? 0);
          }
          return acc;
        }, 0);
      })();

      return dataCount + (value.equationLines?.length ?? 0) + (value.svgPaths?.length ?? 0) > 0;
    },
    {
      message: 'At least one of data, equationLines, or svgPaths is required',
    },
  );

export type PebblePlotScatter = z.infer<typeof pebbleplotScatterSchema>;
export type ScatterDataPoint = z.infer<typeof scatterDataPointSchema>;
export type ScatterDataSet = z.infer<typeof scatterDataSetSchema>;
export type ScatterData = z.infer<typeof scatterDataSchema>;
export type ScatterAxis = z.infer<typeof axisSchema>;
export type ScatterLegend = z.infer<typeof legendSchema>;
export type ScatterLine = z.infer<typeof lineSchema>;
export type EquationLine = z.infer<typeof equationLineSchema>;
export type EquationLineRange = z.infer<typeof equationLineRangeSchema>;
export type SvgPathOverlay = z.infer<typeof svgPathSchema>;
